/**
 * Workspace controller bootstrap — fetch + IDB seed.
 *
 * Replaces the bootstrap logic from useNamedLayouts/useLayoutTree.
 *
 * Flow:
 *   1. Read IDB → hydrate store immediately (instant render).
 *   2. Call adapter.bootstrap() → write fresh IDB doc + meta.
 *   3. If workspaceRevision differs from cached, replace store state from server.
 *   4. Apply the server state to the store.
 *
 * The promise returned is exposed on the store as `bootstrapPromise` so the
 * per-layout content store can await it before deciding whether to fetch
 * `/layouts/{id}` itself.
 */

import type { BootstrapData } from '../types'
import * as workspaceApi from '../workspace-api'
import { trackEvent } from '@/services/analytics/use-analytics'
import { EVENTS } from '@/services/analytics/events'
import type { ConnectionStatus } from './store'
import {
  getWorkspaceMeta,
  getWorkspaceDoc,
  setDocAndMeta,
  setWorkspaceMeta,
  pruneStaleDocs,
} from '../idb-cache'
import type { WorkspaceMeta, WorkspaceDocument } from '@/types/schemas/workspace'
import type { TreeItems } from '../tree-state-manager'
import { ROOT_ID } from '../tree-state-manager'
import type { WorkspaceStore } from './store'
import { SerialQueue } from '../serial-queue'

/**
 * Cap on the IDB cache-hydrate stage of `_init` (Promise.race deadline).
 * A hung `indexedDB.open` (known Safari bfcache bug — the request never
 * resolves nor rejects) must not gate the network fetch: past this deadline
 * the controller proceeds to the server bootstrap without cached state.
 */
export const CACHE_HYDRATE_TIMEOUT_MS = 3000

export interface BootstrapOptions {
  scope: string
  store: WorkspaceStore
  /**
   * When true, the bootstrap response's `activeLayoutId` is ignored: the
   * store and IDB meta keep whatever activeLayoutId the controller already
   * had. Used for cross-tab broadcasts so a sibling tab creating/switching
   * layouts doesn't yank the local user out of what they're viewing.
   *
   * The doc for the server's active layout is still cached to IDB so the
   * user gets an instant render if they navigate to it manually.
   */
  preserveActiveLayout?: boolean
}

function defaultTree(): TreeItems {
  return {
    [ROOT_ID]: { id: ROOT_ID, name: 'root', isFolder: true, children: [] },
  }
}

/**
 * Coerce whatever the bootstrap / cache returned into a TreeItems that has
 * a ROOT_ID entry. The backend returns ``treeItems: {}`` for users who have
 * never created a layout — that's truthy so a plain `?? defaultTree()`
 * lets it through, and the first call to ``createNode(items, ROOT_ID, …)``
 * throws "Parent root not found" (tree-state-manager.ts:158) — which then
 * blows up the onboarding "Choose a layout preset" flow.
 */
function normalizeTreeItems(raw: unknown): TreeItems {
  const items = (raw as TreeItems | undefined | null) ?? null
  if (!items || !items[ROOT_ID]) return defaultTree()
  return items
}

function hasLayouts(items: TreeItems): boolean {
  return Object.values(items).some((n) => !n.isFolder && n.id !== ROOT_ID)
}

/** Ids of every real layout in the tree (folders and ROOT excluded). */
function layoutIdsOf(items: TreeItems): string[] {
  return Object.values(items)
    .filter((n) => !n.isFolder && n.id !== ROOT_ID)
    .map((n) => n.id)
}

/**
 * A document with no tree, no grid, no panels and no window configs carries
 * nothing a user could have authored. Used only to refuse an overwrite: a
 * server response this shape is far more likely to be a transient backend
 * problem than a genuine "the user emptied this layout".
 *
 * `gridLayout` matters. A scroll (grid) layout legitimately has
 * `mosaicTree: null`, so a predicate that only looked at the tree would call
 * a populated grid empty and pin the user to a stale mosaic.
 */
function isDocEmpty(doc: WorkspaceDocument | null | undefined): boolean {
  if (!doc) return true
  const hasTree = doc.mosaicTree != null
  const hasGrid = (doc.gridLayout?.length ?? 0) > 0
  const hasConfigs = Object.keys(doc.windowConfigs ?? {}).length > 0
  const hasPanels = Object.keys(doc.panels ?? {}).length > 0
  return !hasTree && !hasGrid && !hasConfigs && !hasPanels
}

/**
 * Hydrate the store from cached IDB meta. Called synchronously at mount
 * to render last-known state before the network responds.
 *
 * When the cache has layouts, hydration transitions the phase to
 * `Loaded` with the cached revision so the UI renders immediately. The
 * subsequent server bootstrap will either reaffirm or bump the revision.
 */
export async function hydrateFromCache(opts: BootstrapOptions): Promise<void> {
  const cached = await getWorkspaceMeta(opts.scope)
  if (!cached) return
  const items = normalizeTreeItems(cached.treeItems)
  if (!hasLayouts(items)) return
  opts.store.setState((s) => {
    // Late-apply guard: `_init` races this hydrate against
    // CACHE_HYDRATE_TIMEOUT_MS. If the IDB read settles only after the
    // server bootstrap already applied fresh state (phase left Loading),
    // the stale cached snapshot must NOT clobber it.
    if (s.phase.kind !== 'Loading') return s
    return {
      data: {
        ...s.data,
        treeItems: items,
        treeOpenState: cached.treeOpenState ?? [],
        activeLayoutId: cached.activeLayoutId,
      },
      phase: { kind: 'Loaded', workspaceRevision: cached.workspaceRevision ?? 0 },
    }
  })
}

/**
 * Build IDB writes from a bootstrap response. Both doc and meta are written
 * atomically so the per-layout content store can read either without a race.
 */
async function persistBootstrapToIdb(
  scope: string,
  serverActiveId: string,
  data: BootstrapData,
  workspaceRevision: number,
  metaActiveLayoutId: string | null,
): Promise<void> {
  // Layout content (active layout doc) — always keyed by the SERVER's active
  // id. Even if the local user is viewing something else, caching the
  // server's active doc means a manual switch later renders instantly.
  const activeLayoutMaybe = data.activeLayout as
    | {
        panels?: Record<string, unknown>
        layoutRevision?: number
        tabGroups?: WorkspaceDocument['tabGroups']
        layoutMode?: WorkspaceDocument['layoutMode']
        gridLayout?: WorkspaceDocument['gridLayout']
      }
    | null
    | undefined
  const panels = activeLayoutMaybe?.panels
  const doc: WorkspaceDocument = {
    version: 2,
    mosaicTree: (data.activeLayout?.mosaicTree ?? null) as WorkspaceDocument['mosaicTree'],
    windowConfigs: (data.activeLayout?.windowConfigs ?? {}) as WorkspaceDocument['windowConfigs'],
    globalSettings: (data.activeLayout?.globalSettings ??
      {}) as WorkspaceDocument['globalSettings'],
    panels: (panels ?? {}) as WorkspaceDocument['panels'],
    // Carry pane tab-groups through the bootstrap→IDB doc. Without this, every
    // bootstrap rewrites the cached doc without tabGroups, and the content
    // store's validateLayoutData then prunes the tab-member windowConfigs —
    // making tab groups silently vanish on reload.
    ...(activeLayoutMaybe?.tabGroups ? { tabGroups: activeLayoutMaybe.tabGroups } : {}),
    // Same bug class for the scroll (grid) layout fields: dropping them here
    // made a Scroll layout hydrate correctly from IDB, then SNAP to Tiled when
    // the bootstrap rewrite + reconciliation re-read applied this stripped doc
    // (and the next save persisted the loss).
    ...(activeLayoutMaybe?.layoutMode === 'grid'
      ? { layoutMode: 'grid' as const, gridLayout: activeLayoutMaybe.gridLayout ?? [] }
      : {}),
    ...(activeLayoutMaybe?.layoutMode !== 'grid' && activeLayoutMaybe?.gridLayout?.length
      ? { gridLayout: activeLayoutMaybe.gridLayout }
      : {}),
    layoutRevision: activeLayoutMaybe?.layoutRevision,
  }

  const items = normalizeTreeItems(data.treeItems)

  const meta: WorkspaceMeta = {
    version: 2,
    workspaceRevision,
    // Meta's activeLayoutId tracks the LOCAL choice — preserved across
    // cross-tab bootstraps so a sibling tab can't yank this tab into a
    // different layout.
    activeLayoutId: metaActiveLayoutId,
    treeItems: items as WorkspaceMeta['treeItems'],
    treeOpenState: data.treeOpenState ?? [],
  }

  // Empty-overwrite guard. If the server hands us an empty document and the
  // cache holds a populated one, keep the local copy. Meta still advances, so
  // the revision reconciles normally; only the content write is refused.
  //
  // Defensive, not a normal-flow optimization: if this warning appears in the
  // wild it is signalling a server-side problem worth chasing, not a client
  // bug. Restored after being lost with `use-named-layouts.ts`.
  let docToWrite = doc
  if (isDocEmpty(doc)) {
    const existing = await getWorkspaceDoc(scope, serverActiveId)
    if (!isDocEmpty(existing)) {
      const shape = {
        localPanelCount: Object.keys(existing?.panels ?? {}).length,
        localHasMosaicTree: existing?.mosaicTree != null,
        localGridCells: existing?.gridLayout?.length ?? 0,
      }
      console.warn('[workspace] refusing to overwrite populated local doc with empty server doc', {
        scope,
        layoutId: serverActiveId,
        ...shape,
      })
      // The README is explicit: this firing in the wild is a server-side
      // problem worth chasing. A console line in production reaches nobody.
      trackEvent(EVENTS.WORKSPACE_EMPTY_OVERWRITE_REFUSED, {
        workspace_id: scope,
        layout_id: serverActiveId,
        local_panel_count: shape.localPanelCount,
        local_has_mosaic_tree: shape.localHasMosaicTree,
        local_grid_cells: shape.localGridCells,
      })
      docToWrite = existing as WorkspaceDocument
    }
  }

  await setDocAndMeta(scope, serverActiveId, docToWrite, meta)
}

/**
 * Apply a fresh bootstrap response to the controller store. The server is
 * authoritative for tree and revision; activeLayoutId is a local UI
 * choice and is preserved when `preserveActiveLayout` is true.
 */
/**
 * Report a connection transition. Emits only on an actual change: tab
 * re-focus and revision sync both re-run bootstrap and re-assert
 * 'connected', and re-assertions must not become a stream of events. The
 * startup connecting -> connected happens on every page load (about 650 a day
 * in prod, 2026-09-28), so it is skipped too; drops and recoveries remain.
 */
function reportConnectionChange(store: WorkspaceStore, scope: string, to: ConnectionStatus): void {
  const from = store.getState().connectionStatus
  if (from === to) return
  if (from === 'connecting' && to === 'connected') return
  trackEvent(EVENTS.WORKSPACE_CONNECTION_CHANGED, { workspace_id: scope, from, to })
}

function applyBootstrapToStore(
  store: WorkspaceStore,
  scope: string,
  data: BootstrapData,
  workspaceRevision: number,
  resolvedActiveLayoutId: string | null,
): void {
  const items = normalizeTreeItems(data.treeItems)
  reportConnectionChange(store, scope, 'connected')
  store.setState((s) => ({
    data: {
      ...s.data,
      treeItems: items,
      treeOpenState: data.treeOpenState ?? [],
      activeLayoutId: resolvedActiveLayoutId,
      needsOnboarding: !hasLayouts(items),
    },
    phase: { kind: 'Loaded', workspaceRevision },
    connectionStatus: 'connected',
  }))
}

/**
 * Run the bootstrap fetch. Caller (the store factory) holds the returned
 * promise and exposes it on `bootstrapPromise` so the content store can
 * decide whether to skip its own loadLayout.
 */
export async function runBootstrap(opts: BootstrapOptions): Promise<BootstrapData> {
  // Stage 2: fetch the bootstrap. (Templates are chart-owned now — the
  // page's ChartAssetScopeProvider revalidates them on mount.)
  let data: BootstrapData
  try {
    data = await workspaceApi.bootstrap(opts.scope)
  } catch (err) {
    // Disconnect mid-Loading: surface the failure via `connectionStatus`
    // and transition out of `Loading` so consumers can render an error UI
    // instead of an indefinite spinner. If we already had cached data
    // (hydrateFromCache transitioned us to Loaded), leave the phase
    // alone — we still have a revision to point at.
    reportConnectionChange(opts.store, opts.scope, 'disconnected')
    opts.store.setState((s) => ({
      connectionStatus: 'disconnected',
      phase: s.phase.kind === 'Loading' ? { kind: 'Loaded', workspaceRevision: 0 } : s.phase,
    }))
    throw err
  }

  const workspaceRevision = data.workspaceRevision ?? 0

  // Stage 3: revision compare against cache
  const cached = await getWorkspaceMeta(opts.scope)
  const cachedRevision = cached?.workspaceRevision ?? -1

  if (cached && cachedRevision !== workspaceRevision) {
    // Server bumped the revision. Reconcile SURGICALLY: drop only the docs
    // for layouts the server no longer lists.
    //
    // This used to be a full `clearWorkspaceCache(opts.scope)`. That wiped
    // every cached layout doc while the bootstrap response carries content
    // for the ACTIVE layout only, so every other layout lost whatever IDB
    // held for it with nothing to reseed from. Server-confirmed content came
    // back on next access, but IDB-only state did not: a flush whose API call
    // failed, an offline edit, or the optimistic seed of a just-created
    // layout. Note the trigger is routine, not exotic - any sibling-tab
    // create, rename, move or delete bumps the revision (op-policy.ts).
    //
    // Keeping a possibly-stale doc is the safer side of this trade. Each
    // layout's content store independently revalidates on mount: only the
    // ACTIVE layout short-circuits via `bootstrapCovered`, and that path
    // applies fresh server data, not cache. Every other layout falls through
    // to `loadLayout` and reconciles by layoutRevision.
    //
    // Meta is deliberately NOT pruned here: both branches below rewrite it
    // unconditionally (setDocAndMeta, or setWorkspaceMeta for an empty
    // workspace), so there is no window in which stale meta survives.
    await pruneStaleDocs(opts.scope, layoutIdsOf(normalizeTreeItems(data.treeItems)))
  }

  // Resolve the activeLayoutId we want to keep. When preserveActiveLayout
  // is set (cross-tab broadcast), fall back to whatever the local store
  // already has; only use the server's value if the local store hasn't
  // been initialized yet.
  const localActiveId = opts.store.getState().data.activeLayoutId
  const resolvedActiveId =
    opts.preserveActiveLayout && localActiveId ? localActiveId : data.activeLayoutId

  const serverActiveId = data.activeLayoutId
  if (serverActiveId) {
    await persistBootstrapToIdb(
      opts.scope,
      serverActiveId,
      data,
      workspaceRevision,
      resolvedActiveId,
    )
  } else {
    // Empty workspace — just write meta so onboarding flow has something.
    const items = normalizeTreeItems(data.treeItems)
    const meta: WorkspaceMeta = {
      version: 2,
      workspaceRevision,
      activeLayoutId: resolvedActiveId,
      treeItems: items as WorkspaceMeta['treeItems'],
      treeOpenState: data.treeOpenState ?? [],
    }
    await setWorkspaceMeta(opts.scope, meta)
  }

  applyBootstrapToStore(opts.store, opts.scope, data, workspaceRevision, resolvedActiveId)

  return data
}

/**
 * Wraps SerialQueue so all `runBootstrap` invocations for a given store
 * execute sequentially. Solves the cold-start + inbound-broadcast race
 * where two bootstraps could otherwise race-write IDB.
 *
 * Callers pass an optional `shouldRun` predicate (see `./gates.ts`)
 * evaluated at task entry — if a prior queued task already brought
 * local state current, this task no-ops without a server fetch.
 */
export interface EnqueueBootstrapOpts extends BootstrapOptions {
  /** Evaluated at task entry. False → task no-ops, no server fetch. */
  shouldRun?: () => boolean
}

export function makeBootstrapQueue(): {
  enqueueBootstrap: (opts: EnqueueBootstrapOpts) => Promise<BootstrapData | null>
  isBootstrapBusy: () => boolean
} {
  const queue = new SerialQueue()

  function enqueueBootstrap(opts: EnqueueBootstrapOpts): Promise<BootstrapData | null> {
    return new Promise<BootstrapData | null>((resolve, reject) => {
      queue.push<BootstrapData | null>(
        async () => {
          if (opts.shouldRun && !opts.shouldRun()) return null
          return runBootstrap(opts)
        },
        {
          onSuccess: (data) => resolve(data),
          onError: (err) => {
            reject(err)
            return 'drop' as const
          },
        },
      )
    })
  }

  return { enqueueBootstrap, isBootstrapBusy: () => queue.isBusy() }
}
